// REST API client for d3-auth

import { authState } from './auth.js';
import { getBackendUrl } from './backend.js';

class ApiClient {
    constructor() {
        this.baseUrl = getBackendUrl();
    }

    async fetch(path, options = {}) {
        const response = await this._doFetch(path, options);

        // On 401, try refreshing the token and retry once
        if (response.status === 401) {
            await authState._refreshTokens();
            if (authState.getAccessToken()) {
                return this._doFetch(path, options);
            }
        }

        return response;
    }

    async _doFetch(path, options = {}) {
        const url = `${this.baseUrl}${path}`;
        const headers = new Headers(options.headers || {});

        const token = authState.getAccessToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        if (authState.staffTenantId) {
            headers.set('X-Tenant-ID', authState.staffTenantId);
        }
        if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
            headers.set('Content-Type', 'application/json');
        }

        return fetch(url, { ...options, headers });
    }

    async get(path) {
        const response = await this.fetch(path);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status}`);
        }
        return response.json();
    }

    async post(path, data) {
        const options = { method: 'POST' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        const responseData = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(responseData.message || `API error: ${response.status}`);
        }
        return responseData;
    }

    async put(path, data) {
        const options = { method: 'PUT' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status}`);
        }
        return response.json();
    }

    async delete(path) {
        const response = await this.fetch(path, { method: 'DELETE' });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API error: ${response.status}`);
        }
    }
}

export const api = new ApiClient();
